import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import TextArea from "components/inputs/TextArea/TextArea";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import { useDeleteMetadocMetadataMutation } from "services/metadoc-api";

import AddCodePresetModal from "./AddCodePresetModal";
import CodeReferenceList from "./CodeReferenceList";

import type { ChangeEvent } from "react";
import type {
  AddCodePreset,
  CodeSection,
  CodeStatus,
  EditableCodeValue,
  SupportedCodeType,
} from "../../utils/codeMetadataUtils";
import { CODE_SECTION_LABELS } from "../../utils/codeMetadataUtils";

type TextAreaChangeEvent = ChangeEvent<HTMLTextAreaElement> | string;

type CodeCardProps = {
  codeValue: EditableCodeValue;
  onCodePresetSave: (
    metadataId: number,
    preset: AddCodePreset,
  ) => Promise<void>;
  onConditionTypeChange: (
    metadataId: number,
    conditionType: CodeStatus,
  ) => void;
  onSectionChange: (metadataId: number, section: CodeSection) => Promise<void>;
  onFieldChange: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes" | "reference" | "pageNumber",
    value: string,
  ) => void;
  onFieldSave: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes",
    value: string,
  ) => Promise<void>;
  onAssociationChange: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => void;
  onAssociationSave: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => Promise<void>;
  getReferenceContext: (args: {
    pageNumber: string;
    documentGroupId?: number | null;
    facility?: string | null;
    provider?: string | null;
  }) => {
    resolvedDocumentGroupId: number | null;
    resolvedFacility: string;
    provider: string;
  };
};

const getTextAreaValue = (event: TextAreaChangeEvent): string =>
  typeof event === "string" ? event : event.target.value;

const getEditableCodeType = (
  codeType: EditableCodeValue["codeType"],
): SupportedCodeType => codeType || "ICD-10";

/*
renders a single code card with editing capabilities
includes array safety checks for date details
*/
const CodeCard = ({
  codeValue,
  onCodePresetSave,
  onConditionTypeChange,
  onSectionChange,
  onFieldChange,
  onFieldSave,
  onAssociationChange,
  onAssociationSave,
  getReferenceContext,
}: CodeCardProps) => {
  const [isCodePresetModalOpen, setIsCodePresetModalOpen] = useState(false);
  const [isSavingCodePreset, setIsSavingCodePreset] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [deleteMetadocMetadata, { isLoading: isDeletingCode }] =
    useDeleteMetadocMetadataMutation();

  const selectedCodeDisplay = codeValue.codeType
    ? `${codeValue.codeType}: ${codeValue.code || "N/A"}`
    : (codeValue.code ?? "N/A");

  const handleCodePresetSave = async (preset: AddCodePreset) => {
    setIsSavingCodePreset(true);
    try {
      await onCodePresetSave(codeValue.id, preset);
      setIsCodePresetModalOpen(false);
    } finally {
      setIsSavingCodePreset(false);
    }
  };

  const handleDeleteCode = async () => {
    try {
      await deleteMetadocMetadata({ metadataId: codeValue.id }).unwrap();
      setIsDeleteModalOpen(false);
      setIsDeleted(true);
    } catch (error) {
      console.error("Failed to delete code metadata:", error);
      KaboodleToaster.error("Failed to delete code. Please try again.");
    }
  };

  const handleProviderChange = (provider: string): void => {
    onAssociationChange(
      codeValue.id,
      codeValue.documentGroupId,
      provider,
      codeValue.facility,
    );
  };

  const handleProviderSave = async (provider: string): Promise<void> => {
    await onAssociationSave(
      codeValue.id,
      codeValue.documentGroupId,
      provider,
      codeValue.facility,
    );
  };

  const handleFacilityChange = (facility: string): void => {
    onAssociationChange(
      codeValue.id,
      codeValue.documentGroupId,
      codeValue.provider,
      facility,
    );
  };

  const handleFacilitySave = async (facility: string): Promise<void> => {
    await onAssociationSave(
      codeValue.id,
      codeValue.documentGroupId,
      codeValue.provider,
      facility,
    );
  };

  if (isDeleted) {
    return null;
  }

  const deleteCodeModal = (
    <KaboodleModal
      isOpen={isDeleteModalOpen}
      onClose={() => {
        if (!isDeletingCode) {
          setIsDeleteModalOpen(false);
        }
      }}
      footer={
        <div className="flex jc-c">
          <Button
            className="delete"
            onClick={handleDeleteCode}
            disabled={isDeletingCode}
          >
            {isDeletingCode ? "Deleting..." : "Confirm Delete"}
          </Button>
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false);
            }}
            disabled={isDeletingCode}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Delete Code">
        <p>Are you sure you want to delete this code?</p>
        <p>This action cannot be undone.</p>
      </Card>
    </KaboodleModal>
  );

  if (codeValue.dateDetails.length === 0) {
    return (
      <>
        <Card
          heading={null}
          type="inverse"
          classNames="metadoc-viewer-workspace__code-card"
        >
          <div className="metadoc-viewer-workspace__code-card-content">
            <div className="metadoc-viewer-workspace__code-main">
              <div className="metadoc-viewer-workspace__code-top">
                <div className="metadoc-viewer-workspace__code-field metadoc-viewer-workspace__code-field--compact">
                  <span className="metadoc-viewer-workspace__code-field-label">
                    Code
                  </span>
                  <span className="metadoc-viewer-workspace__code-field-value">
                    {selectedCodeDisplay}
                  </span>
                  <button
                    type="button"
                    className="metadoc-viewer-workspace__code-edit-trigger"
                    onClick={() => {
                      setIsCodePresetModalOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="metadoc-viewer-workspace__code-edit-trigger"
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                    }}
                    disabled={isDeletingCode}
                  >
                    Delete
                  </button>
                </div>
                <div className="metadoc-viewer-workspace__code-notes">
                  No details available
                </div>
              </div>
            </div>
          </div>
        </Card>
        <AddCodePresetModal
          isOpen={isCodePresetModalOpen}
          isSubmitting={isSavingCodePreset}
          title="Update Code"
          submitLabel="Save Code"
          initialPreset={{
            codeType: getEditableCodeType(codeValue.codeType),
            code: codeValue.code || "",
            description: "",
          }}
          onClose={() => {
            setIsCodePresetModalOpen(false);
          }}
          onSubmit={handleCodePresetSave}
        />
        {deleteCodeModal}
      </>
    );
  }

  return (
    <>
      <Card
        heading={null}
        type="inverse"
        classNames="metadoc-viewer-workspace__code-card"
      >
        <div className="metadoc-viewer-workspace__code-card-content">
          <div className="metadoc-viewer-workspace__code-main">
            <div className="metadoc-viewer-workspace__code-top">
              <div className="metadoc-viewer-workspace__code-field-col">
                <div className="metadoc-viewer-workspace__code-field metadoc-viewer-workspace__code-field--compact">
                  <span className="metadoc-viewer-workspace__code-field-label">
                    Code
                  </span>
                  <span className="metadoc-viewer-workspace__code-field-value">
                    {selectedCodeDisplay}
                  </span>
                  <button
                    type="button"
                    className="metadoc-viewer-workspace__code-edit-trigger"
                    onClick={() => {
                      setIsCodePresetModalOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="metadoc-viewer-workspace__code-edit-trigger"
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                    }}
                    disabled={isDeletingCode}
                  >
                    Delete
                  </button>
                </div>
                <div className="metadoc-viewer-workspace__code-section-row">
                  <span className="metadoc-viewer-workspace__code-field-label">
                    Section
                  </span>
                  <div className="metadoc-viewer-workspace__code-section-select-wrapper">
                    <select
                      id={`code-${codeValue.id}-section`}
                      className="metadoc-viewer-workspace__code-section-select"
                      value={codeValue.section}
                      onChange={(e) => {
                        void onSectionChange(
                          codeValue.id,
                          e.target.value as CodeSection,
                        );
                      }}
                    >
                      {(
                        Object.entries(CODE_SECTION_LABELS) as [
                          CodeSection,
                          string,
                        ][]
                      ).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <span className="metadoc-viewer-workspace__code-section-arrow material-icons">
                      expand_more
                    </span>
                  </div>
                </div>
              </div>
              <div className="metadoc-viewer-workspace__code-notes">
                <TextArea
                  label="Description"
                  name={`code-${codeValue.id}-description`}
                  value={codeValue.dateDetails[0].description}
                  readOnly
                  onChange={(event) => {
                    onFieldChange(
                      codeValue.id,
                      codeValue.dateDetails[0].id,
                      "description",
                      getTextAreaValue(event),
                    );
                  }}
                  onSave={async (value) => {
                    await onFieldSave(
                      codeValue.id,
                      codeValue.dateDetails[0].id,
                      "description",
                      value,
                    );
                  }}
                />
              </div>
              <div className="metadoc-viewer-workspace__code-notes">
                <TextArea
                  label="Note"
                  name={`code-${codeValue.id}-notes`}
                  value={codeValue.dateDetails[0].notes}
                  advancedEdit
                  onChange={(event) => {
                    onFieldChange(
                      codeValue.id,
                      codeValue.dateDetails[0].id,
                      "notes",
                      getTextAreaValue(event),
                    );
                  }}
                  onSave={async (value) => {
                    await onFieldSave(
                      codeValue.id,
                      codeValue.dateDetails[0].id,
                      "notes",
                      value,
                    );
                  }}
                />
              </div>
            </div>
            <CodeReferenceList
              codeId={codeValue.id}
              dateDetails={codeValue.dateDetails}
              documentGroupId={codeValue.documentGroupId}
              provider={codeValue.provider}
              facility={codeValue.facility}
              onDateChange={(detailId, value) => {
                onFieldChange(codeValue.id, detailId, "date", value);
              }}
              onDateSave={async (detailId, value) => {
                await onFieldSave(codeValue.id, detailId, "date", value);
              }}
              onProviderChange={handleProviderChange}
              onProviderSave={handleProviderSave}
              onFacilityChange={handleFacilityChange}
              onFacilitySave={handleFacilitySave}
              getReferenceContext={getReferenceContext}
            />
          </div>
          <div className="metadoc-viewer-workspace__code-status">
            <span className="metadoc-viewer-workspace__code-field-label">
              Status
            </span>
            <div className="metadoc-viewer-workspace__code-status-options">
              <div className="metadoc-viewer-workspace__code-status-option">
                <Checkbox
                  label="A"
                  name={`code-${codeValue.id}-accepted`}
                  checked={codeValue.conditionType === "accepted"}
                  onChange={() => {
                    onConditionTypeChange(codeValue.id, "accepted");
                  }}
                />
              </div>
              <div className="metadoc-viewer-workspace__code-status-option">
                <Checkbox
                  label="D"
                  name={`code-${codeValue.id}-denied`}
                  checked={codeValue.conditionType === "denied"}
                  onChange={() => {
                    onConditionTypeChange(codeValue.id, "denied");
                  }}
                />
              </div>
              <div className="metadoc-viewer-workspace__code-status-option">
                <Checkbox
                  label="U"
                  name={`code-${codeValue.id}-unrelated`}
                  checked={codeValue.conditionType === "unrelated"}
                  onChange={() => {
                    onConditionTypeChange(codeValue.id, "unrelated");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      <AddCodePresetModal
        isOpen={isCodePresetModalOpen}
        isSubmitting={isSavingCodePreset}
        title="Update Code"
        submitLabel="Save Code"
        initialPreset={{
          codeType: getEditableCodeType(codeValue.codeType),
          code: codeValue.code || "",
          description: codeValue.dateDetails[0].description || "",
        }}
        onClose={() => {
          setIsCodePresetModalOpen(false);
        }}
        onSubmit={handleCodePresetSave}
      />
      {deleteCodeModal}
    </>
  );
};

export default CodeCard;
